import {
  APP_IDS,
  emptyAppMrr,
  emptyBuilderFlags,
  londonDate,
  type AppId,
  type BuilderFlags,
  type VillageMetrics,
} from "../engine/src/index.ts";

export type AdapterResult = {
  metrics: VillageMetrics;
  missing: string[];
  unallocatedMrrUsd: number;
  warnings: string[];
};

type Json = Record<string, unknown>;

function isRecord(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readPath(root: unknown, path: string): unknown {
  let current: unknown = root;
  for (const key of path.split(".")) {
    if (!isRecord(current) || !(key in current)) {
      return undefined;
    }
    current = current[key];
  }
  return current;
}

function firstDefined(root: unknown, paths: readonly string[]): unknown {
  for (const path of paths) {
    const value = readPath(root, path);
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === "true" || value === "yes" || value === 1) {
    return true;
  }
  if (value === "false" || value === "no" || value === 0) {
    return false;
  }
  return undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

const METRIC_PATHS = {
  date: ["date", "generatedDate", "day", "meta.date"],
  planMilestonesHit: [
    "planner.milestonesHit",
    "planner.ninetyDay.milestonesHit",
    "plan.milestonesHit",
    "lifePlanner.milestones",
    "milestones.hit",
    "milestonesHit",
  ],
  applicationsSent: [
    "search.linkedinApplications",
    "search.applications",
    "applications.linkedin",
    "applications.sent",
    "applications.total",
    "jobs.applications",
    "linkedin.applications",
  ],
  interviewsLanded: [
    "search.interviews",
    "applications.interviews",
    "jobs.interviews",
    "interviews",
  ],
  offersReceived: ["search.offers", "applications.offers", "jobs.offers", "offers"],
  sideHustleGbpPerMonth: [
    "money.sideHustleGbpPerMonth",
    "money.sideHustleGbp",
    "money.gbpPerMonth",
    "sideHustle.gbp",
    "money.monthlyGbp",
  ],
  totalMrrUsd: ["mrr.totalUsd", "mrr.total", "apps.totalMrrUsd", "revenue.totalMrrUsd"],
  lessonsDone: ["upskill.lessonsDone", "upskill.lessons", "lab.lessons", "lessons.done"],
  socialPosts: [
    "socials.posts",
    "socials.baseerah.posts",
    "baseerah.posts",
    "social.posts",
  ],
  socialFollowers: [
    "socials.followers",
    "socials.baseerah.followers",
    "baseerah.followers",
    "social.followers",
  ],
  bugsFixed: ["debug.bugsFixed", "bugs.fixed", "walls.bugsFixed", "debug.bugs"],
  dailyActionsDoneYesterday: [
    "motivator.dailyActionsDoneYesterday",
    "motivator.yesterdayDone",
    "motivator.dailyActionsDone",
    "daily.actionsDoneYesterday",
    "daily.yesterdayDone",
  ],
  dailyActionsTotal: [
    "motivator.dailyActionsTotal",
    "motivator.total",
    "daily.actionsTotal",
    "motivator.dailyActions",
  ],
} as const;

const APP_MRR_PATHS: Record<AppId, string[]> = {
  revenue: [
    "mrr.apps.Revenue",
    "mrr.apps.revenue",
    "apps.revenue.mrrUsd",
    "apps.Revenue.mrr",
    "apps.revenue.mrr",
  ],
  viralz: [
    "mrr.apps.Viralz",
    "mrr.apps.viralz",
    "apps.viralz.mrrUsd",
    "apps.Viralz.mrr",
    "apps.viralz.mrr",
  ],
  tikwave: [
    "mrr.apps.Tikwave",
    "mrr.apps.tikwave",
    "mrr.apps.TikWave",
    "apps.tikwave.mrrUsd",
    "apps.Tikwave.mrr",
    "apps.tikwave.mrr",
  ],
  gogrow: [
    "mrr.apps.GoGrow",
    "mrr.apps.gogrow",
    "apps.gogrow.mrrUsd",
    "apps.GoGrow.mrr",
    "apps.gogrow.mrr",
  ],
  rihla: [
    "mrr.apps.Rihla",
    "mrr.apps.rihla",
    "apps.rihla.mrrUsd",
    "apps.Rihla.mrr",
    "apps.rihla.mrr",
  ],
};

const BUILDER_PATHS: Record<keyof BuilderFlags, string[]> = {
  repoMade: ["builder.repoMade", "builder.repo", "workshop.repoMade"],
  firstDeploy: ["builder.firstDeploy", "builder.deploy", "workshop.firstDeploy"],
  mvpLive: ["builder.mvpLive", "builder.mvp", "workshop.mvpLive"],
  firstUser: ["builder.firstUser", "workshop.firstUser"],
  firstPayingUser: [
    "builder.firstPayingUser",
    "builder.payingUser",
    "workshop.firstPayingUser",
  ],
};

function readNumber(
  raw: unknown,
  key: keyof typeof METRIC_PATHS,
  missing: string[],
  fallback = 0,
): number {
  const value = asNumber(firstDefined(raw, METRIC_PATHS[key]));
  if (value === undefined) {
    missing.push(key);
    return fallback;
  }
  return value;
}

function readBoolean(
  raw: unknown,
  key: "dailyActionsDoneYesterday",
  missing: string[],
  fallback = false,
): boolean {
  const value = asBoolean(firstDefined(raw, METRIC_PATHS[key]));
  if (value === undefined) {
    missing.push(key);
    return fallback;
  }
  return value;
}

export function adaptDashboard(raw: unknown, at: Date = new Date()): AdapterResult {
  const missing: string[] = [];
  const warnings: string[] = [];

  if (!isRecord(raw)) {
    return {
      metrics: emptyMetrics(londonDate(at)),
      missing: Object.keys(METRIC_PATHS).concat(APP_IDS).concat(Object.keys(BUILDER_PATHS)),
      unallocatedMrrUsd: 0,
      warnings: ["dashboard.json was not an object"],
    };
  }

  const date =
    asString(firstDefined(raw, METRIC_PATHS.date)) ??
    (asString(raw.generatedAt) ? londonDate(new Date(String(raw.generatedAt))) : londonDate(at));

  const appMrrUsd = emptyAppMrr();
  for (const appId of APP_IDS) {
    const value = asNumber(firstDefined(raw, APP_MRR_PATHS[appId]));
    if (value === undefined) {
      missing.push(`appMrrUsd.${appId}`);
      appMrrUsd[appId] = 0;
    } else {
      appMrrUsd[appId] = value;
    }
  }

  const builder = emptyBuilderFlags();
  for (const flag of Object.keys(BUILDER_PATHS) as (keyof BuilderFlags)[]) {
    const value = asBoolean(firstDefined(raw, BUILDER_PATHS[flag]));
    if (value === undefined) {
      missing.push(`builder.${flag}`);
      builder[flag] = false;
    } else {
      builder[flag] = value;
    }
  }

  const totalMrrUsd = readNumber(raw, "totalMrrUsd", missing, 0);
  const summed = APP_IDS.reduce((sum, id) => sum + appMrrUsd[id], 0);
  const unallocatedMrrUsd = Math.max(0, Math.round((totalMrrUsd - summed) * 100) / 100);
  if (unallocatedMrrUsd > 0) {
    warnings.push(
      `$${unallocatedMrrUsd} combined MRR is not attributed to Revenue/Viralz/Tikwave/GoGrow/Rihla`,
    );
  }

  const metrics: VillageMetrics = {
    date,
    planMilestonesHit: readNumber(raw, "planMilestonesHit", missing),
    applicationsSent: readNumber(raw, "applicationsSent", missing),
    interviewsLanded: readNumber(raw, "interviewsLanded", missing),
    offersReceived: readNumber(raw, "offersReceived", missing),
    sideHustleGbpPerMonth: readNumber(raw, "sideHustleGbpPerMonth", missing),
    appMrrUsd,
    totalMrrUsd,
    lessonsDone: readNumber(raw, "lessonsDone", missing),
    builder,
    socialPosts: readNumber(raw, "socialPosts", missing),
    socialFollowers: readNumber(raw, "socialFollowers", missing),
    bugsFixed: readNumber(raw, "bugsFixed", missing),
    dailyActionsDoneYesterday: readBoolean(raw, "dailyActionsDoneYesterday", missing),
    dailyActionsTotal: readNumber(raw, "dailyActionsTotal", missing),
  };

  return { metrics, missing, unallocatedMrrUsd, warnings };
}

export function emptyMetrics(date: string): VillageMetrics {
  return {
    date,
    planMilestonesHit: 0,
    applicationsSent: 0,
    interviewsLanded: 0,
    offersReceived: 0,
    sideHustleGbpPerMonth: 0,
    appMrrUsd: emptyAppMrr(),
    totalMrrUsd: 0,
    lessonsDone: 0,
    builder: emptyBuilderFlags(),
    socialPosts: 0,
    socialFollowers: 0,
    bugsFixed: 0,
    dailyActionsDoneYesterday: false,
    dailyActionsTotal: 0,
  };
}
